import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { getUserLevel } from '../../services/UserService';
import { supabase } from '../../config/supabase';
import authService from '../../services/AuthServiceWrapper';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeToggle from '../widgets/ThemeToggle';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
`;

const Content = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
`;

const SettingsSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: left;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: ${props => props.theme.typography.h4.fontWeight};
  margin-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding-bottom: ${props => props.theme.spacing.sm};
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.div`
  color: ${props => props.theme.colors.text.primary};
  font-weight: 500;
`;

const SettingDescription = styled.div`
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.small.fontSize};
  margin-top: 2px;
`;

const Settings = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [pointsHistory, setPointsHistory] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    setCurrentUser(authService.currentUser);
  }, []);

  useEffect(() => {
    async function fetchPointsHistory() {
      if (currentUser) {
        const { data, error } = await supabase
          .from('scene_points_history')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });
        if (!error) setPointsHistory(data);
      }
    }
    fetchPointsHistory();
  }, [currentUser]);

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
          Back
        </BackButton>
        <Title>Settings</Title>
      </Header>
      
      <Content>
        <SettingsSection>
          <SectionTitle>Appearance</SectionTitle>
          <SettingItem>
            <div>
              <SettingLabel>Theme</SettingLabel>
              <SettingDescription>Choose between light and dark mode</SettingDescription>
            </div>
            <ThemeToggle showLabel={false} />
          </SettingItem>
        </SettingsSection>

        <SettingsSection>
          <SectionTitle>Account</SectionTitle>
          {currentUser && (
            <SettingItem>
              <div>
                <SettingLabel>User Level</SettingLabel>
                <SettingDescription>
                  <span style={{ fontSize: '1.2em' }}>{getUserLevel(currentUser.scenePoints).icon}</span>
                  <span style={{ marginLeft: '8px', fontWeight: 600 }}>{getUserLevel(currentUser.scenePoints).name}</span>
                  <span style={{ color: theme.colors.text.muted, marginLeft: '8px' }}>{currentUser.scenePoints} pts</span>
                </SettingDescription>
              </div>
            </SettingItem>
          )}
        </SettingsSection>

        {currentUser && pointsHistory.length > 0 && (
          <SettingsSection>
            <SectionTitle>Scene Points History</SectionTitle>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {pointsHistory.map((entry) => (
                <SettingItem key={entry.id}>
                  <div>
                    <SettingLabel>
                      <strong>{entry.points_change > 0 ? '+' : ''}{entry.points_change}</strong> pts
                    </SettingLabel>
                    <SettingDescription>
                      {entry.reason} • {new Date(entry.created_at).toLocaleString()}
                    </SettingDescription>
                  </div>
                </SettingItem>
              ))}
            </div>
          </SettingsSection>
        )}
      </Content>
    </Container>
  );
};

export default Settings; 